import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Contact, VavClient } from '@vav/client-core';
import type { CallManager, CallPeer } from '../call.ts';
import { startRing } from '../ringtone.ts';
import { Avatar } from './Avatar.tsx';
import { useT } from '../i18n.tsx';

/**
 * The whole life of a call on one overlay: ringing in, ringing out, talking.
 *
 * Mounted at the app root rather than inside the thread, because a call
 * outlives navigation: switching conversations mid-call must not hang up.
 */
export function CallOverlay({
  manager,
  client,
  contactsById,
  profileLabel,
}: {
  manager: CallManager;
  client: VavClient;
  /** Optional: the overlay falls back to the client's own contact records. */
  contactsById?: Record<string, Contact>;
  /** Set when this call belongs to a profile other than the one on screen. */
  profileLabel?: string;
}) {
  const t = useT();
  const state = useSyncExternalStore(
    (onChange) => manager.subscribe(onChange),
    () => manager.state,
  );

  // A short "call ended" note, self-dismissing.
  useEffect(() => {
    if (state.phase === 'idle' && state.ended) {
      const timer = setTimeout(() => manager.dismissEnded(), 2600);
      return () => clearTimeout(timer);
    }
  }, [state.phase, state.ended, manager]);

  // Ring while ringing; see ringtone.ts for why this can actually sound.
  useEffect(() => {
    if (state.phase !== 'incoming' && state.phase !== 'outgoing') return;
    return startRing(state.phase);
  }, [state.phase]);

  /**
   * Where the sound goes. A phone call belongs at the ear, not on the table:
   * when the platform exposes an earpiece output the call starts there, and a
   * button moves it to the speaker. Browsers that expose no outputs (most
   * desktops, some Androids) simply never show the button.
   */
  const [speakerId, setSpeakerId] = useState<string>();
  const [onSpeaker, setOnSpeaker] = useState(false);

  useEffect(() => {
    if (state.phase !== 'active') {
      setOnSpeaker(false);
      return;
    }
    void (async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outs = devices.filter(
          (device) => device.kind === 'audiooutput' && device.deviceId && device.deviceId !== 'default',
        );
        // The default route stays whatever the platform chose (Chen's spec:
        // a call does not open on speakers). The button's job is only to
        // reach the loudspeaker on demand: by label where labels are
        // English, and by "the second output" where they are localised.
        const byLabel = outs.find((device) => /speaker/i.test(device.label));
        const speaker = byLabel ?? (outs.length >= 2 ? outs[outs.length - 1] : undefined);
        setSpeakerId(speaker?.deviceId);
      } catch {
        // No device list, no routing choice.
      }
    })();
  }, [state.phase]);

  const sinkId =
    state.phase === 'active' && speakerId !== undefined ? (onSpeaker ? speakerId : '') : undefined;

  if (state.phase === 'idle') {
    if (!state.ended) return null;
    const note = {
      declined: t('call.ended.declined'),
      busy: t('call.ended.busy'),
      hangup: t('call.ended.hangup'),
      failed: t('call.ended.failed'),
      unanswered: t('call.ended.unanswered'),
    }[state.ended];
    return <div className="call__note" role="status">{note}</div>;
  }

  const contactOf = (userId?: string) =>
    userId ? (contactsById?.[userId] ?? client.getContact(userId)) : undefined;
  const nameOf = (userId?: string) => (userId ? (contactOf(userId)?.displayName ?? userId) : '');

  if (state.phase === 'incoming') {
    return (
      <div className="overlay call">
        <div className="call__card">
          <Avatar name={nameOf(state.from)} size={96} avatar={contactOf(state.from)?.avatar} client={client} />
          <strong className="call__name">{nameOf(state.from)}</strong>
          <span className="call__hint">
            {state.media === 'video' ? t('call.incoming.video') : t('call.incoming.audio')}
          </span>
          {profileLabel && (
            <span className="call__profile">{t('call.forProfile', { name: profileLabel })}</span>
          )}
          <div className="call__actions">
            <button className="call__button call__button--accept" onClick={() => void manager.accept()}>
              {state.media === 'video' ? '🎥' : '📞'} {t('call.accept')}
            </button>
            <button className="call__button call__button--hangup" onClick={() => void manager.decline()}>
              ✕ {t('call.decline')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state.phase === 'outgoing') {
    const ringee = state.peers[0]?.userId;
    return (
      <div className="overlay call">
        <div className="call__card">
          <Avatar name={nameOf(ringee) || '…'} size={96} avatar={contactOf(ringee)?.avatar} client={client} />
          <strong className="call__name">{nameOf(ringee) || t('call.outgoing')}</strong>
          <span className="call__hint">{t('call.outgoing')}</span>
          <div className="call__actions">
            <button className="call__button call__button--hangup" onClick={() => void manager.hangup()}>
              ✕ {t('call.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active.
  return (
    <div className="overlay call">
      <div className="call__stage">
        <div className={`call__tiles call__tiles--${Math.min(state.peers.length, 3)}`}>
          {state.peers.map((peer) => (
            <PeerTile key={peer.userId} peer={peer} name={nameOf(peer.userId)} video={state.media === 'video'} client={client} contact={contactOf(peer.userId)} sinkId={sinkId} />
          ))}
        </div>

        {state.media === 'video' && state.localStream && !state.cameraOff && (
          <LocalPreview stream={state.localStream} />
        )}

        <CallClock startedAt={state.startedAt} />

        {/* The diagnosis line: when a leg failed and the server has no TURN,
            say exactly that — a screenshot of this row settles the question
            "why do calls not work between our phones". */}
        {state.peers.some((p) => p.failed) && (
          <p className="call__failNote" role="alert">
            {t('call.legFailed')}
            {state.relay === false && <> · {t('call.turnMissing')}</>}
          </p>
        )}

        <div className="call__controls">
          <button
            className={`call__button ${state.muted ? 'call__button--engaged' : ''}`}
            onClick={() => manager.toggleMute()}
            aria-pressed={state.muted}
          >
            {state.muted ? '🔇' : '🎙️'} {state.muted ? t('call.unmute') : t('call.mute')}
          </button>
          {state.media === 'video' && (
            <button
              className={`call__button ${state.cameraOff ? 'call__button--engaged' : ''}`}
              onClick={() => manager.toggleCamera()}
              aria-pressed={state.cameraOff}
            >
              {state.cameraOff ? '🚫' : '📷'} {state.cameraOff ? t('call.cameraOn') : t('call.cameraOff')}
            </button>
          )}
          {speakerId !== undefined && (
            <button
              className={`call__button ${onSpeaker ? 'call__button--engaged' : ''}`}
              onClick={() => setOnSpeaker((v) => !v)}
              aria-pressed={onSpeaker}
            >
              {onSpeaker ? '📱' : '🔊'} {onSpeaker ? t('call.toEarpiece') : t('call.toSpeaker')}
            </button>
          )}
          <button className="call__button call__button--hangup" onClick={() => void manager.hangup()}>
            ✕ {t('call.hangup')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PeerTile({
  peer,
  name,
  video,
  client,
  contact,
  sinkId,
}: {
  peer: CallPeer;
  name: string;
  video: boolean;
  client: VavClient;
  contact?: Contact;
  sinkId?: string;
}) {
  const t = useT();
  const mediaRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (mediaRef.current && peer.stream) mediaRef.current.srcObject = peer.stream;
  }, [peer.stream]);

  useEffect(() => {
    const element = mediaRef.current;
    if (element && sinkId !== undefined && 'setSinkId' in element) {
      void element.setSinkId(sinkId).catch(() => undefined);
    }
  }, [sinkId]);

  const hasVideo = video && peer.stream && peer.stream.getVideoTracks().some((track) => track.enabled);

  return (
    <div className="call__tile">
      {/* One <video> either way: it is also the audio sink, and remote audio
          must keep playing when the camera is off. */}
      <video
        ref={mediaRef}
        autoPlay
        playsInline
        className={hasVideo ? 'call__video' : 'call__video call__video--hidden'}
      />
      {!hasVideo && (
        <div className="call__tileFallback">
          <Avatar name={name} size={72} avatar={contact?.avatar} client={client} />
        </div>
      )}
      <span className="call__tileName">
        {name}
        {peer.failed ? (
          <em className="call__tileFailed"> · {t('call.legFailed')}</em>
        ) : (
          !peer.connected && <em> · {t('call.connecting')}</em>
        )}
      </span>
    </div>
  );
}

function LocalPreview({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return <video ref={ref} autoPlay playsInline muted className="call__self" />;
}

function CallClock({ startedAt }: { startedAt?: number }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  if (!startedAt) return null;
  const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return (
    <span className="call__clock" dir="ltr">
      {mm}:{ss}
    </span>
  );
}
